import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, FormField, Input, RichTextEditor } from '../../components/ui/index.js';
import { aiGenerate } from '../../lib/aiContent.js';
import { api } from '../../lib/api.js';
import { useEnabledLanguages, useLanguagesStore } from '../../lib/languagesStore.js';

export interface TranslatableField {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'richtext';
}

interface Props {
  entityType: string;
  /** Omit for the singleton cms_settings (the server keys it on the tenant). */
  entityId?: string;
  fields: TranslatableField[];
  disabled?: boolean;
  /**
   * Default-language source values per field key. When provided (the AI module
   * is on), shows a "Translate with AI" button that fills the active language.
   */
  aiSources?: Record<string, string | null | undefined>;
  /**
   * DEFERRED mode (create flow — the entity doesn't exist yet): the parent owns
   * the values (locale → field → value) and persists them AFTER it creates the
   * entity. No API GET/PUT here, and no Save button. Provide `value` + `onChange`.
   */
  deferred?: boolean;
  value?: Record<string, Record<string, string>>;
  onChange?: (all: Record<string, Record<string, string>>) => void;
}

/**
 * Reusable editor for an entity's NON-default-language content. The default
 * language value lives in the entity's own form/table; this writes the overlay
 * in translatable_strings via /cms/translations, for every enabled language
 * except the default. Drop it into any editor.
 */
export function TranslationEditor({ entityType, entityId, fields, disabled, aiSources, deferred, value, onChange }: Props): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const defaultLang = useLanguagesStore((s) => s.data.default);
  // Translate every enabled language except the default (which is the base row).
  const langs = useEnabledLanguages().filter((l) => l.code !== defaultLang);
  const [locale, setLocale] = useState<string>('');
  const [all, setAll] = useState<Record<string, Record<string, string>>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const aiAvailable = !!aiSources && Object.values(aiSources).some((v) => v && String(v).trim());

  // In deferred (create) mode the parent owns the values; otherwise this component
  // fetches + saves them itself keyed on the entity id.
  const store = deferred ? (value ?? {}) : all;
  const ready = deferred || entityType === 'cms_settings' || !!entityId;
  const params = entityType === 'cms_settings' ? { entityType } : { entityType, entityId };

  useEffect(() => {
    if (!ready || deferred) return;
    void api().get<{ translations: Record<string, Record<string, string>> }>('/cms/translations', { params })
      .then((r) => setAll(r.data.translations))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  // Keep the active tab on a valid (enabled, non-default) language.
  useEffect(() => {
    if (langs.length > 0 && !langs.some((l) => l.code === locale)) setLocale(langs[0]!.code);
  }, [langs, locale]);

  // Non-deferred: the editable buffer for the active language is a local draft
  // (committed to the API on Save). Deferred reads straight from the parent value.
  useEffect(() => { if (!deferred) setDraft(all[locale] ?? {}); }, [locale, all, deferred]);

  const fieldVal = (k: string): string => (deferred ? (store[locale]?.[k] ?? '') : (draft[k] ?? ''));
  const setField = (k: string, v: string): void => {
    if (deferred) onChange?.({ ...store, [locale]: { ...(store[locale] ?? {}), [k]: v } });
    else setDraft((d) => ({ ...d, [k]: v }));
  };

  // AI-translate the default-language sources into the active language (fills the
  // draft; the user reviews + saves). Per-field, preserving HTML for richtext.
  const aiTranslate = async (): Promise<void> => {
    if (!locale || !aiSources) return;
    setTranslating(true);
    try {
      const next: Record<string, string> = { ...(deferred ? (store[locale] ?? {}) : draft) };
      for (const f of fields) {
        const src = aiSources[f.key];
        if (!src || !String(src).trim()) continue;
        const r = await aiGenerate({ task: 'translate', source: String(src), targetLang: locale, html: f.type === 'richtext' });
        if (r.text) next[f.key] = r.text;
      }
      if (deferred) onChange?.({ ...store, [locale]: next });
      else setDraft(next);
      toast.success(t('translate.aiFilled', { defaultValue: 'Translated — review and save.' }));
    } catch {
      toast.error(t('translate.aiFailed', { defaultValue: 'Could not translate — check Settings → AI.' }));
    } finally {
      setTranslating(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!locale) return;
    setSaving(true);
    try {
      const payloadFields: Record<string, string | null> = {};
      for (const f of fields) {
        const v = draft[f.key];
        payloadFields[f.key] = v && v.trim() ? v : null;
      }
      const r = await api().put<{ translations: Record<string, Record<string, string>> }>(
        '/cms/translations', { ...params, locale, fields: payloadFields },
      );
      setAll(r.data.translations);
      toast.success(t('translate.saved'));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  if (!ready) return <p className="text-xs text-fg-muted">{t('translate.saveFirst')}</p>;
  if (langs.length === 0) {
    return (
      <p className="text-xs text-fg-muted">
        {t('translate.noLanguages', { defaultValue: 'Enable more languages in Settings → Languages to translate this content.' })}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-muted">{t('translate.language')}</span>
        {langs.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setLocale(l.code)}
            aria-pressed={locale === l.code}
            dir={l.isRtl ? 'rtl' : 'ltr'}
            className={`rounded-md border px-2.5 py-1 text-xs ${locale === l.code ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
          >
            {l.nativeName}
          </button>
        ))}
        {aiAvailable && !disabled && (
          <Button type="button" variant="secondary" size="sm" className="ms-auto" loading={translating} disabled={!locale || saving} onClick={() => void aiTranslate()}>
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {t('translate.ai', { defaultValue: 'Translate with AI' })}
          </Button>
        )}
      </div>
      {fields.map((f) => (
        <FormField key={f.key} id={`tr-${entityType}-${f.key}`} label={f.label}>
          {f.type === 'richtext' ? (
            <RichTextEditor id={`tr-${entityType}-${f.key}`} value={fieldVal(f.key)} onChange={(v) => setField(f.key, v)} ariaLabel={f.label} />
          ) : f.type === 'textarea' ? (
            <textarea
              id={`tr-${entityType}-${f.key}`}
              value={fieldVal(f.key)}
              onChange={(e) => setField(f.key, e.target.value)}
              disabled={disabled}
              rows={2}
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
            />
          ) : (
            <Input id={`tr-${entityType}-${f.key}`} value={fieldVal(f.key)} onChange={(e) => setField(f.key, e.target.value)} disabled={disabled} />
          )}
        </FormField>
      ))}
      {!disabled && !deferred && (
        <Button type="button" variant="secondary" className="w-fit" loading={saving} onClick={() => void save()}>
          {t('translate.save')}
        </Button>
      )}
      {deferred && (
        <p className="text-xs text-fg-muted">
          {t('translate.savedWithItem', { defaultValue: 'Translations are saved together with the item.' })}
        </p>
      )}
    </div>
  );
}
